/**
 * ClassHierarchyBrowser.tsx
 *
 * Hierarchical tree view of ontology classes
 * Shows parent-child relationships with expand/collapse
 */

import React, { useState } from 'react';

type OntologyNode = {
  id: string;
  label: string;
  type: 'Class' | 'Individual' | 'Property';
  parent?: string;
  pillar?: 'F' | 'A' | 'I' | 'R';
  meta?: Record<string, unknown>;
};

interface ClassHierarchyBrowserProps {
  nodes: OntologyNode[];
  onNodeSelect?: (nodeId: string) => void;
  selectedNodeId?: string;
}

interface TreeNode {
  node: OntologyNode;
  children: TreeNode[];
  depth: number;
}

export function ClassHierarchyBrowser({
  nodes,
  onNodeSelect,
  selectedNodeId
}: ClassHierarchyBrowserProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['Dataset']));

  // Build tree structure
  const tree = buildTree(nodes);

  const toggleNode = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const renderTreeNode = (treeNode: TreeNode) => {
    const { node, children, depth } = treeNode;
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = children.length > 0;
    const isSelected = node.id === selectedNodeId;

    return (
      <div key={node.id} style={{ marginLeft: `${depth * 20}px` }}>
        <div
          className="tree-node"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            cursor: 'pointer',
            backgroundColor: isSelected ? '#e0f2fe' : 'transparent',
            borderRadius: '4px',
            marginBottom: '2px'
          }}
          onClick={() => {
            if (hasChildren) toggleNode(node.id);
            if (onNodeSelect) onNodeSelect(node.id);
          }}
        >
          {hasChildren ? (
            <span style={{ marginRight: '8px', fontSize: '12px' }}>
              {isExpanded ? '▼' : '▶'}
            </span>
          ) : (
            <span style={{ marginRight: '8px', width: '12px' }} />
          )}

          <span
            className="node-icon"
            style={{
              marginRight: '8px',
              fontSize: '16px',
              color: getNodeColor(node)
            }}
          >
            {getNodeIcon(node.type)}
          </span>

          <span
            style={{
              fontWeight: isSelected ? '600' : '400',
              fontSize: '14px'
            }}
          >
            {node.label}
          </span>

          {node.pillar && (
            <span
              className="pillar-badge"
              style={{
                marginLeft: '8px',
                padding: '2px 6px',
                fontSize: '11px',
                fontWeight: '600',
                color: getPillarColor(node.pillar),
                backgroundColor: `${getPillarColor(node.pillar)}20`,
                borderRadius: '4px'
              }}
            >
              {node.pillar}
            </span>
          )}
        </div>

        {isExpanded && children.map(child => renderTreeNode(child))}
      </div>
    );
  };

  return (
    <div className="class-hierarchy-browser" style={{
      padding: '16px',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      maxHeight: '600px',
      overflowY: 'auto'
    }}>
      <h3 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
        Class Hierarchy
      </h3>

      <div className="tree-container">
        {tree.map(treeNode => renderTreeNode(treeNode))}
      </div>
    </div>
  );
}

/**
 * Build tree structure from flat node list
 */
function buildTree(nodes: OntologyNode[]): TreeNode[] {
  const nodeMap = new Map<string, TreeNode>();
  const roots: TreeNode[] = [];

  // Create tree nodes
  for (const node of nodes) {
    nodeMap.set(node.id, {
      node,
      children: [],
      depth: 0
    });
  }

  // Build parent-child relationships
  for (const node of nodes) {
    const treeNode = nodeMap.get(node.id)!;

    if (node.parent) {
      const parent = nodeMap.get(node.parent);
      if (parent) {
        parent.children.push(treeNode);
        treeNode.depth = parent.depth + 1;
      } else {
        roots.push(treeNode);
      }
    } else {
      roots.push(treeNode);
    }
  }

  return roots;
}

/**
 * Get icon for node type
 */
function getNodeIcon(type: string): string {
  switch (type) {
    case 'Class':
      return '📦';
    case 'Individual':
      return '🔹';
    case 'Property':
      return '🔗';
    default:
      return '●';
  }
}

/**
 * Get color for node type
 */
function getNodeColor(node: OntologyNode): string {
  switch (node.type) {
    case 'Class':
      return '#3b82f6'; // blue
    case 'Individual':
      return '#a855f7'; // purple
    case 'Property':
      return '#22c55e'; // green
    default:
      return '#6b7280'; // gray
  }
}

/**
 * Get color for pillar
 */
function getPillarColor(pillar: 'F' | 'A' | 'I' | 'R'): string {
  const colors = {
    F: '#3b82f6', // blue
    A: '#22c55e', // green
    I: '#f59e0b', // amber
    R: '#ec4899'  // pink
  };
  return colors[pillar];
}
